import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../services/apiService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const queryClient = useQueryClient();

    useEffect(() => {
        // Check for existing token on mount
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
        }
        setLoading(false);
    }, []);

    const clearSessionData = () => {
        // Clear TanStack Query cache
        queryClient.clear();

        // Clear axios headers if any were set manually (though we use interceptors)
        // localStorage is already handled in logout
    };

    const login = async (credentials) => {
        try {
            const response = await authAPI.login(credentials);
            const { data } = response.data;

            if (data.requiresOTP) {
                return {
                    success: true,
                    requiresOTP: true,
                    email: data.email,
                    phone: data.phone
                };
            }

            const { token: newToken, user: newUser } = data;

            // Clear any previous session data before setting new user
            clearSessionData();

            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(newUser));

            setToken(newToken);
            setUser(newUser);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    };

    const verifyOTP = async (email, otp) => {
        try {
            const response = await authAPI.verifyOTP({ email, otp });
            const { token: newToken, user: newUser } = response.data.data;

            // Clear any previous session data
            clearSessionData();

            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(newUser));

            setToken(newToken);
            setUser(newUser);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'OTP verification failed'
            };
        }
    };

    const register = async (userData) => {
        try {
            const response = await authAPI.register(userData);
            const { token: newToken, user: newUser } = response.data.data;

            // Clear any previous session data
            clearSessionData();

            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(newUser));

            setToken(newToken);
            setUser(newUser);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Registration failed'
            };
        }
    };

    const logout = () => {
        clearSessionData();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
    };

    const updateUser = (updatedUser) => {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    const resetPassword = async (data) => {
        try {
            const response = await authAPI.resetPassword(data);
            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password reset failed'
            };
        }
    };

    const forgotPassword = async (email) => {
        try {
            const response = await authAPI.forgotPassword({ email });
            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to send reset code'
            };
        }
    };

    const value = {
        user,
        token,
        loading,
        login,
        verifyOTP,
        register,
        logout,
        updateUser,
        forgotPassword,
        resetPassword,
        isAuthenticated: !!token,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
